import z from "zod";
import { ImageSchema, LanguageEnum } from "../common";

export const BlogTranslationSchema = z.object({
  id: z.uuidv7(),
  blogId: z.uuidv7(),
  locale: LanguageEnum,
  title: z.string().min(1),
  content: z.string().min(1),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  slug: z.string().nullable().optional(),
  publicationStatus: z.enum(["draft", "published"]).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  image: z.string().optional(),
  altText: z.string().optional(),
  keywords: z.string().array(),
});

export const emptyBlogTranslation = {
  title: "",
  content: "",
  keywords: [],
  publicationStatus: "draft" as const,
};

export function hasBlogTranslationContent(value: { title: string; content: string }) {
  const title = value.title.trim().toLowerCase();
  const content = value.content
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim()
    .toLowerCase();

  return title !== "" && title !== "n/a" && content !== "" && content !== "n/a";
}

const CreateBlogTranslationShape = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  image: ImageSchema,
  altText: z.string().optional(),
  keywords: z.string().array(),
  slug: z.string().trim().min(1).nullable().optional(),
  publicationStatus: z.enum(["draft", "published"]).optional(),
});

const RequiredCreateBlogTranslationShape = CreateBlogTranslationShape.refine(hasBlogTranslationContent, {
  message: "Title and content are required",
});

const OptionalCreateBlogTranslationShape = CreateBlogTranslationShape.extend({
  title: z.string(),
  content: z.string(),
}).superRefine((value, context) => {
  const isEmpty =
    value.title.trim() === "" &&
    value.content
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim() === "";
  if (!isEmpty && !hasBlogTranslationContent(value)) {
    context.addIssue({ code: "custom", message: "Complete both the title and content, or leave both empty" });
  }
  if (isEmpty && value.publicationStatus === "published") {
    context.addIssue({ code: "custom", message: "An empty translation cannot be published" });
  }
});

export const CreateBlogTranslationSchema = z.object({
  en: RequiredCreateBlogTranslationShape,
  ar: RequiredCreateBlogTranslationShape,
  tr: OptionalCreateBlogTranslationShape,
  fa: OptionalCreateBlogTranslationShape,
});

const UpdateBlogTranslationShape = z.object({
  id: z.uuidv7().optional(),
  title: z.string().min(1),
  content: z.string().min(1),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  image: ImageSchema,
  altText: z.string().optional(),
  keywords: z.string().array(),
  slug: z.string().trim().min(1).nullable().optional(),
  publicationStatus: z.enum(["draft", "published"]).optional(),
});

const RequiredUpdateBlogTranslationShape = UpdateBlogTranslationShape.refine(hasBlogTranslationContent, {
  message: "Title and content are required",
});

const OptionalUpdateBlogTranslationShape = UpdateBlogTranslationShape.extend({
  title: z.string(),
  content: z.string(),
}).superRefine((value, context) => {
  const isEmpty =
    value.title.trim() === "" &&
    value.content
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim() === "";
  if (!isEmpty && !hasBlogTranslationContent(value)) {
    context.addIssue({ code: "custom", message: "Complete both the title and content, or leave both empty" });
  }
  if (isEmpty && value.publicationStatus === "published") {
    context.addIssue({ code: "custom", message: "An empty translation cannot be published" });
  }
});

export const UpdateBlogTranslationSchema = z.object({
  en: RequiredUpdateBlogTranslationShape,
  ar: RequiredUpdateBlogTranslationShape,
  tr: OptionalUpdateBlogTranslationShape,
  fa: OptionalUpdateBlogTranslationShape,
});

export const SeedBlogTranslationSchema = z.object({
  id: z.uuidv7(),
  locale: LanguageEnum,
  title: z.string().min(1),
  content: z.string().min(1),
  altText: z.string().optional(),
});
